import React from 'react';
import { FileText, AlertTriangle, Mail, Phone, MapPin, Clock } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import Seo from './Seo';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-cyan-50">
      <Seo title="Terms of Service" path="/terms-of-service" description="Terms and conditions for Bubbles & Bark grooming services." />
      <Header showContactInfo={true} showLoginButton={true} />
      
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <FileText className="w-8 h-8 text-green-600" />
            <h1 className="text-4xl font-bold text-gray-800">Terms of Service</h1>
          </div>
          <p className="text-lg text-gray-600">
            Please read these terms carefully before using our pet grooming services.
          </p>
          <p className="text-sm text-gray-500 mt-2">Last updated: January 2025</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-3xl shadow-xl p-8 space-y-8">
          
          {/* Acceptance of Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-green-600 font-bold text-sm">1</span>
              </div>
              Acceptance of Terms
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>
                By using Bubbles And Bark's services, creating an account, or booking appointments, 
                you agree to be bound by these Terms of Service and our Privacy Policy. If you do not 
                agree with these terms, please do not use our services.
              </p>
              <p>
                These terms apply to all users, including pet owners, service providers, and visitors 
                to our website and mobile applications.
              </p>
            </div>
          </section>

          {/* Service Description */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-blue-600 font-bold text-sm">2</span>
              </div>
              Our Services
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>Bubbles And Bark provides premium pet grooming services including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Signature grooming packages with full styling</li>
                <li>Quick grooming services for essential care</li>
                <li>Refresh bath and spa treatments</li>
                <li>Specialized cat grooming services</li>
                <li>Individual services (nail trimming, ear cleaning, etc.)</li>
                <li>Specialty treatments and add-on services</li>
              </ul>
              <p>
                All services are performed by professional groomers at our Orlando, FL
                location.
              </p>
            </div>
          </section>

          {/* User Responsibilities */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-purple-600 font-bold text-sm">3</span>
              </div>
              Your Responsibilities
            </h2>
            <div className="space-y-4 text-gray-700">
              <h3 className="text-lg font-semibold text-gray-800">Pet Health & Safety</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Ensure your pet is up-to-date on all required vaccinations</li>
                <li>Disclose any health conditions, allergies, or behavioral issues</li>
                <li>Inform us of any medications your pet is taking</li>
                <li>Provide accurate emergency contact information</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6">Account & Booking</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide accurate and current contact information</li>
                <li>Arrive on time for scheduled appointments</li>
                <li>Give 24-hour notice for cancellations when possible</li>
                <li>Pay for services promptly and in full</li>
                <li>Treat our staff and facilities with respect</li>
              </ul>
            </div>
          </section>

          {/* Booking & Payment Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-orange-600 font-bold text-sm">4</span>
              </div>
              Booking & Payment
            </h2>
            <div className="space-y-4 text-gray-700">
              <h3 className="text-lg font-semibold text-gray-800">Reservations</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Appointments are confirmed upon payment or valid payment method on file</li>
                <li>Walk-ins are welcome but subject to availability</li>
                <li>We reserve the right to refuse service if pet appears ill or aggressive</li>
                <li>Pricing may vary based on coat condition, pet behavior, and time required</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6">Payment</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Payment is due at time of service or booking</li>
                <li>We accept credit cards, debit cards, and digital payments</li>
                <li>All payments are processed securely through Stripe</li>
                <li>Gratuities are appreciated but not required</li>
              </ul>
            </div>
          </section>

          {/* Cancellation & No-Show Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-red-600 font-bold text-sm">5</span>
              </div>
              Cancellation & No-Show Policy
            </h2>
            <div className="space-y-4 text-gray-700">
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                <div className="flex">
                  <AlertTriangle className="w-5 h-5 text-red-400 mr-2 mt-0.5" />
                  <div>
                    <p className="font-semibold text-red-800">Important Fee Information</p>
                    <ul className="mt-2 space-y-1 text-red-700">
                      <li>• $25 fee for cancellations with less than 24-hour notice</li>
                      <li>• $25 fee for no-shows or unavailable pets at appointment time</li>
                      <li>• $25 fee for late pickups (more than 60 minutes after completion)</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6">Free Cancellation</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Cancel 24+ hours in advance with no fee</li>
                <li>Reschedule appointments without penalty when proper notice is given</li>
                <li>Emergency situations will be considered on a case-by-case basis</li>
              </ul>
            </div>
          </section>

          {/* Liability & Disclaimers */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-yellow-600 font-bold text-sm">6</span>
              </div>
              Liability & Disclaimers
            </h2>
            <div className="space-y-4 text-gray-700">
              <h3 className="text-lg font-semibold text-gray-800">Our Commitment</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>All groomers are certified professionals</li>
                <li>We use only high-quality, pet-safe products</li>
                <li>We treat every pet with care and individual attention</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6">Limitations</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>We are not responsible for pre-existing health conditions</li>
                <li>Some pets may experience stress-related reactions to grooming</li>
                <li>Matted coats may require additional time and cost</li>
                <li>We reserve the right to stop service if a pet becomes aggressive</li>
                <li>Results may vary based on individual pet characteristics</li>
              </ul>
            </div>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-indigo-600 font-bold text-sm">7</span>
              </div>
              Intellectual Property
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>
                All content on our website, mobile app, and marketing materials, including text, 
                graphics, logos, and software, is owned by Bubbles And Bark and protected by copyright law.
              </p>
              <h3 className="text-lg font-semibold text-gray-800 mt-4">Photo Policy</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>We may take photos of pets during grooming for our records</li>
                <li>Photos may be used for marketing purposes with your consent</li>
                <li>You may request that photos not be taken or used</li>
                <li>All photos remain our property unless otherwise agreed</li>
              </ul>
            </div>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-pink-600 font-bold text-sm">8</span>
              </div>
              Termination
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>
                We reserve the right to terminate or suspend your account and refuse service at our 
                discretion, including for:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Violation of these terms of service</li>
                <li>Abusive or disrespectful behavior toward staff</li>
                <li>Repeated no-shows or late cancellations</li>
                <li>Providing false or misleading information</li>
                <li>Failure to pay for services</li>
              </ul>
            </div>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-cyan-600 font-bold text-sm">9</span>
              </div>
              Changes to Terms
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>
                We may update these terms of service periodically. We will notify you of significant 
                changes through email or prominent notices on our website. Your continued use of our 
                services after changes indicates acceptance of the updated terms.
              </p>
            </div>
          </section>

          {/* Contact Information */}
          <section className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-2xl p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <Mail className="w-6 h-6 text-green-600 mr-3" />
              Questions About Terms
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>If you have questions about these terms of service, please contact us:</p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <span>info@bubblesbark.com</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-green-600" />
                    <span>(407) 868-8608</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <MapPin className="w-5 h-5 text-red-600" />
                    <span>12188 Lake Underhill Rd<br />Orlando, FL 32825</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Clock className="w-5 h-5 text-purple-600" />
                    <div>
                      <span className="font-semibold">Mon-Fri: 9AM-5PM</span>
                      <p className="text-sm text-gray-600">Sat: 9AM-6PM, Sun: 10AM-3PM</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}